import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { ShoppingCart, Eye, Star } from "lucide-react";
import { useCart } from "../contexts/CartContext";

const ProductCard = ({ product }) => {
  const { addToCart, formatPrice } = useCart();
  
  // Use _id if available, fallback to id
  const productId = product._id || product.id;  
  
  const primaryImage = product.images && product.images.length > 0 
    ? product.images[0].src 
    : "https://placehold.co/300/300";

  const price = parseFloat(product.price || product.regular_price || 0);
  const regularPrice = parseFloat(product.regular_price || 0);
  const salePrice = product.sale_price ? parseFloat(product.sale_price) : null;

  const isOnSale = salePrice && salePrice < regularPrice;
  const displayPrice = isOnSale ? salePrice : price;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const handleCallForPrice = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Call Umoona Opal Mine phone number
    window.location.href = 'tel:+61886724588'; // (08) 8672-4588
  };

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
      <Link to={`/opal-store/product/${productId}`} className="block">
        <div className="aspect-square relative overflow-hidden bg-white">
          <img 
            src={primaryImage}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = "https://placehold.co/300/300";
            }}
          />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {isOnSale && (
              <Badge className="bg-red-600 hover:bg-red-700 text-white">
                SALE
              </Badge>
            )}
            {product.featured && (
              <Badge className="bg-purple-600 hover:bg-purple-700 text-white">
                <Star className="w-3 h-3 mr-1" />
                FEATURED
              </Badge>
            )}
            {product.stock_status === 'outofstock' && (
              <Badge className="bg-gray-600 text-white">
                OUT OF STOCK
              </Badge>
            )}
          </div>

          {/* Quick Actions */}
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/90 hover:bg-white shadow-md"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.href = `/opal-store/product/${productId}`;
                }}
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Add to Cart Button - Bottom overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {product.stock_status === 'instock' && displayPrice > 0 ? (
              <Button 
                onClick={handleAddToCart}
                size="sm" 
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add to Cart
              </Button>
            ) : displayPrice === 0 ? (
              <Button 
                onClick={handleCallForPrice}
                size="sm" 
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              >
                📞 Call for Price
              </Button>
            ) : (
              <Button 
                disabled
                size="sm" 
                className="w-full"
              >
                Out of Stock
              </Button>
            )}
          </div>
        </div>
      </Link>

      <CardContent className="p-4 space-y-3">
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-900 line-clamp-2 hover:text-purple-600 transition-colors">
            {product.name}
          </h3>
          
          {product.sku && (
            <p className="text-xs text-gray-500 font-mono">SKU: {product.sku}</p>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOnSale ? (
                <>
                  <span className="text-lg font-bold text-purple-600">
                    ${salePrice.toFixed(2)}
                  </span>
                  <span className="text-sm text-gray-500 line-through">
                    ${regularPrice.toFixed(2)}
                  </span>
                </>
              ) : displayPrice > 0 ? (
                <span className="text-lg font-bold text-purple-600">
                  ${displayPrice.toFixed(2)}
                </span>
              ) : (
                <button 
                  onClick={handleCallForPrice}
                  className="text-lg font-bold text-orange-600 hover:text-orange-700 underline cursor-pointer"
                >
                  📞 Call for Price
                </button>
              )}
            </div>
            
            {product.stock_quantity && (
              <span className="text-sm text-gray-500">
                {product.stock_quantity} in stock
              </span>
            )}
          </div>

          {product.categories && product.categories.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {product.categories.slice(0, 2).map((category) => (
                <Badge 
                  key={category.id} 
                  variant="outline" 
                  className="text-xs text-purple-600 border-purple-200"
                >
                  {category.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductCard;